/**
 * React Collection Product Widget
 * Clean, maintainable React components for Shopify collection pages
 * Using JSX-like syntax with helper functions for better readability
 */

(function () {
    'use strict';

    // Wait for React to be loaded via the promise
    function initWidget() {
        // Exit early if React is not available
        if (typeof React === 'undefined' || typeof ReactDOM === 'undefined') {
            console.error('React or ReactDOM not loaded');
            return;
        }

        const { useState, useEffect, useMemo, createElement } = React;

        // JSX-like helper functions for better readability
        const jsx = (type, props, ...children) => {
            const flatChildren = children.flat().filter(child => child !== null && child !== undefined);
            return createElement(type, props, ...flatChildren);
        };

        // HTML element shortcuts
        const div = (props, ...children) => jsx('div', props, ...children);
        const span = (props, ...children) => jsx('span', props, ...children);
        const p = (props, ...children) => jsx('p', props, ...children);
        const h1 = (props, ...children) => jsx('h1', props, ...children);
        const h2 = (props, ...children) => jsx('h2', props, ...children);
        const h3 = (props, ...children) => jsx('h3', props, ...children);
        const img = (props) => jsx('img', props);
        const a = (props, ...children) => jsx('a', props, ...children);
        const select = (props, ...children) => jsx('select', props, ...children);
        const option = (props, ...children) => jsx('option', props, ...children);
        const button = (props, ...children) => jsx('button', props, ...children);
        const nav = (props, ...children) => jsx('nav', props, ...children);
        const label = (props, ...children) => jsx('label', props, ...children);
        const br = () => jsx('br');

        // =============================================================================
        // UTILITIES
        // =============================================================================

        /**
         * Format price with proper currency formatting
         */
        const formatPrice = (price, currency = 'USD') => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(price / 100);
        };

        /**
         * Get unique values from array and sort them
         */
        const getUniqueValues = (items, key) => {
            return [...new Set(items.map(item => item[key]).filter(Boolean))].sort();
        };

        /**
         * SKU Filter Configuration
         * New format: SS-VP-4OZ-12PK or SS-[FLAVOR]-[SIZE]-[PACK]
         * Maps filter labels to SKU patterns
         * NOTE: We only filter by SIZE (oz), not by product type (VP = Variety Pack)
         * This prevents duplicate cards for variety pack products
         */
        const SKU_FILTER_CONFIG = {
            '1.5 oz': ['1.5OZ', '1OZ'], // Match 1.5OZ format
            '4 oz': ['4OZ']
            // 'Variety Pack': ['VP'] - REMOVED: VP is a product type, not a size
        };

        /**
         * Check if a SKU matches a pattern
         * New format uses contains logic for segment matching (e.g., "4OZ" in "SS-VP-4OZ-12PK")
         */
        const skuMatchesPattern = (sku, pattern) => {
            if (!sku || !pattern) return false;

            // Convert to uppercase for case-insensitive matching
            const upperSku = sku.toUpperCase();
            const upperPattern = pattern.toUpperCase();

            // Check if the pattern exists as a segment in the SKU
            return upperSku.includes(upperPattern);
        };

        /**
         * Get available SKU filters based on products
         * Only returns filters that have matching products
         */
        const getAvailableSKUFilters = (products) => {
            const availableFilters = [];

            Object.entries(SKU_FILTER_CONFIG).forEach(([filterLabel, patterns]) => {
                const hasMatchingProduct = products.some(product => {
                    if (!product.variants || product.variants.length === 0) return false;
                    return product.variants.some(variant => {
                        if (!variant.sku) return false;
                        const matches = patterns.some(pattern => skuMatchesPattern(variant.sku, pattern));
                        return matches;
                    });
                });

                if (hasMatchingProduct) {
                    availableFilters.push(filterLabel);
                }
            });

            return availableFilters;
        };

        /**
         * Get SKU filter label for a product based on its variants
         * Prioritizes currently selected filters
         */
        const getSKUFilterForProduct = (product, selectedFilters = []) => {
            if (!product.variants || product.variants.length === 0) return '';

            // Find all matching filters for this product
            const matchingFilters = [];

            for (const [filterLabel, patterns] of Object.entries(SKU_FILTER_CONFIG)) {
                const hasMatch = product.variants.some(variant => {
                    if (!variant.sku) return false;
                    return patterns.some(pattern => skuMatchesPattern(variant.sku, pattern));
                });

                if (hasMatch) {
                    matchingFilters.push(filterLabel);
                }
            }

            // If we have selected filters, prioritize showing the selected one that matches
            if (selectedFilters && selectedFilters.length > 0) {
                for (const selectedFilter of selectedFilters) {
                    if (matchingFilters.includes(selectedFilter)) {
                        return selectedFilter;
                    }
                }
            }

            // Otherwise return the first matching filter
            return matchingFilters[0] || '';
        };

        /**
         * Check if a product matches SKU filter patterns
         */
        const productMatchesSKUFilter = (product, filterLabels) => {
            if (!filterLabels || filterLabels.length === 0) return true;
            if (!product.variants || product.variants.length === 0) return false;

            return filterLabels.some(filterLabel => {
                const patterns = SKU_FILTER_CONFIG[filterLabel];
                if (!patterns) return false;

                return product.variants.some(variant => {
                    if (!variant.sku) return false;
                    return patterns.some(pattern => skuMatchesPattern(variant.sku, pattern));
                });
            });
        };

        /**
         * Get size category for a variant based on its SKU
         * Returns array of all matching filters (e.g., ["Variety Pack", "4 oz"])
         */
        const getSizeForVariant = (variant) => {
            if (!variant.sku) return [];

            const matchingFilters = [];
            for (const [filterLabel, patterns] of Object.entries(SKU_FILTER_CONFIG)) {
                if (patterns.some(pattern => skuMatchesPattern(variant.sku, pattern))) {
                    matchingFilters.push(filterLabel);
                }
            }
            return matchingFilters;
        };

        /**
         * Check if a product/variant is a Variety Pack
         * Detects VP in the SKU to identify variety pack products
         */
        const isVarietyPack = (skuOrProduct) => {
            let sku = '';

            // Handle both SKU strings and product/variant objects
            if (typeof skuOrProduct === 'string') {
                sku = skuOrProduct;
            } else if (skuOrProduct?.sku) {
                sku = skuOrProduct.sku;
            } else if (skuOrProduct?.variants?.[0]?.sku) {
                sku = skuOrProduct.variants[0].sku;
            }

            return sku.toUpperCase().includes('VP');
        };

        /**
         * Expand products into individual variant cards grouped by size
         * Creates separate product cards for each size variant
         */
        const expandProductsByVariant = (products, showAllProducts = true) => {
            const expandedProducts = [];

            products.forEach(product => {
                if (!product.variants || product.variants.length === 0) {
                    // If no variants, keep the original product
                    expandedProducts.push(product);
                    return;
                }

                // Group variants by size - now handling arrays of sizes
                const variantsBySize = {};
                product.variants.forEach(variant => {
                    const sizes = getSizeForVariant(variant); // Returns array now
                    if (sizes && sizes.length > 0) {
                        // Add variant to each size category it matches
                        sizes.forEach(size => {
                            if (!variantsBySize[size]) {
                                variantsBySize[size] = [];
                            }
                            variantsBySize[size].push(variant);
                        });
                    }
                });

                // If no size variants found but showAllProducts is true, keep original product
                if (Object.keys(variantsBySize).length === 0) {
                    if (showAllProducts) {
                        expandedProducts.push(product);
                    }
                    return;
                }

                // Create a product card for each size variant
                Object.entries(variantsBySize).forEach(([size, variants]) => {
                    const expandedProduct = {
                        ...product,
                        id: `${product.id}_${size.replace(/\s+/g, '_')}`, // Unique ID for each size variant
                        variants: variants,
                        variantSize: size, // Store the size for display
                        // Use the first variant's price as the main price for the card
                        price: variants[0]?.price || product.price,
                        compare_at_price: variants[0]?.compare_at_price || product.compare_at_price,
                        available: variants.some(v => v.available) // Available if any variant is available
                    };
                    expandedProducts.push(expandedProduct);
                });
            });

            return expandedProducts;
        };

        // =============================================================================
        // COMPONENTS
        // =============================================================================

        /**
         * Product Card Component
         * Renders individual product with image, title, price, and badges
         * Now with flip functionality to show additional details on the back
         */
        const ProductCard = ({ product, showVendor = false, showQuickBuy = true, showComparePrice = true, selectedSKUFilters = [] }) => {
            const [isFlipped, setIsFlipped] = useState(false);
            const [selectedVariantIndex, setSelectedVariantIndex] = useState(0); // Track selected variant
            const [isAddingToCart, setIsAddingToCart] = useState(false); // Track loading state

            // Set default selected variant to first available variant when product changes
            useEffect(() => {
                if (product.variants && product.variants.length > 0) {
                    setSelectedVariantIndex(0);
                }
            }, [product.id]);

            // Extract custom badge from p_badge_* tags
            const customBadge = product.tags?.find(tag => tag.startsWith('p_badge_'))?.replace('p_badge_', '') || null;

            const handleImageClick = (e) => {
                // Navigate to product page when clicking the image
                const selectedVariant = product.variants?.[selectedVariantIndex];
                const baseUrl = `/products/${product.handle}`;
                const url = selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                window.location.href = url;
            };

            const handleQuickShopClick = (e) => {
                // Flip the card when clicking the QUICK SHOP button
                e.preventDefault();
                e.stopPropagation();
                setIsFlipped(true);
            };

            const handleCloseFlip = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsFlipped(false);
            };

            const handleQuickAdd = async (e) => {
                e.preventDefault();
                e.stopPropagation();

                const selectedVariant = product.variants?.[selectedVariantIndex];
                if (!selectedVariant) {
                    alert('Please select a variant.');
                    return;
                }

                if (!selectedVariant.available) {
                    alert('Sorry, this variant is out of stock.');
                    return;
                }

                setIsAddingToCart(true);

                try {
                    // Get the root path for API calls
                    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

                    // Prepare sections to bundle (similar to theme's ProductForm)
                    let sectionsToBundle = ["cart-drawer"];
                    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
                        bubbles: true,
                        detail: { sections: sectionsToBundle }
                    }));

                    // Use FormData like the theme's ProductForm does
                    const formData = new FormData();
                    formData.set('id', selectedVariant.id);
                    formData.set('quantity', '1');
                    formData.set('sections', sectionsToBundle.join(","));
                    formData.set('sections_url', `${rootPath}variants/${selectedVariant.id}`);

                    const response = await fetch(`${rootPath}cart/add.js`, {
                        method: 'POST',
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: formData
                    });

                    const responseJson = await response.json();

                    if (response.ok) {

                        // Get updated cart content (like theme's ProductForm)
                        const cartContent = await (await fetch(`${rootPath}cart.js`)).json();
                        cartContent["sections"] = responseJson["sections"];

                        // Trigger the same events as the theme's ProductForm
                        document.documentElement.dispatchEvent(new CustomEvent("variant:add", {
                            bubbles: true,
                            detail: {
                                items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
                                cart: cartContent
                            }
                        }));

                        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
                            bubbles: true,
                            detail: {
                                baseEvent: "variant:add",
                                cart: cartContent
                            }
                        }));

                        // Check if using cart page or cart drawer
                        if (window.themeVariables && window.themeVariables.settings.cartType === "page") {
                            window.location.href = `${rootPath}cart`;
                        } else {
                            // Cart drawer should open automatically via the theme's event listeners
                            // No need to manually trigger it - the theme handles this
                        }
                    } else {
                        console.error('Cart add error:', responseJson);

                        // Dispatch cart error event like the theme does
                        document.documentElement.dispatchEvent(new CustomEvent("cart:error", {
                            bubbles: true,
                            detail: {
                                error: responseJson["description"] || responseJson["message"]
                            }
                        }));

                        alert(responseJson.message || responseJson.description || 'Failed to add item to cart.');
                    }
                } catch (error) {
                    console.error('Cart add failed:', error);
                    alert('Failed to add item to cart. Please try again.');
                } finally {
                    setIsAddingToCart(false);
                }
            };

            return div({
                className: `product-card ${isFlipped ? 'product-card--flipped' : ''} ${product.variantSize ? 'product-card--variant' : ''}`,
                style: { perspective: '1000px' }
            },
                div({ className: 'product-card__inner' },
                    // FRONT SIDE
                    div({
                        className: 'product-card__front'
                    },
                        // Product image with badges (clickable - navigates to product page)
                        div({
                            className: 'product-card__figure',
                            onClick: handleImageClick,
                            style: { cursor: 'pointer' }
                        },
                            img({
                                src: (() => {
                                    // Try to get the selected variant's image first
                                    const selectedVariant = product.variants?.[selectedVariantIndex];
                                    if (selectedVariant?.image) {
                                        return selectedVariant.image;
                                    }
                                    // Fallback to product's featured image
                                    return product.featured_image;
                                })(),
                                alt: product.title,
                                className: 'product-card__image',
                                loading: 'lazy',
                                width: '300',
                                height: '300'
                            }),

                            // Badge container for top right positioning
                            div({ className: 'product-card__badge-list' },
                                // Custom badge only (sale badge removed)
                                customBadge && span({
                                    className: 'badge badge--custom',
                                    'aria-label': customBadge
                                }, customBadge.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                            )
                        ),

                        div({ className: 'product-card__header' },
                            // Product title with SKU filter
                            p({ className: 'product-card__header-left' },
                                (() => {
                                    // For variant-specific products, show the variant size
                                    if (product.variantSize) {
                                        return `SUNFLOWER SEEDS | ${product.variantSize}`;
                                    }

                                    // Otherwise, extract SKU filter for the product (fallback)
                                    const skuFilter = getSKUFilterForProduct(product, selectedSKUFilters);
                                    const formattedSKUFilter = skuFilter ? ` | ${skuFilter}` : '';
                                    return `SUNFLOWER SEEDS${formattedSKUFilter}`;
                                })()
                            ),

                            // Only render header-right if there's bag count content
                            (() => {
                                // SKU bag count mapping for new format: 11PK, 12PK, 36PK, 54PK, etc.
                                const SKU_BAG_MAPPING = {
                                    '6PK': '6',
                                    '11PK': '11',
                                    '12PK': '12',
                                    '14PK': '12 Bags + 2 Free',
                                    '36PK': '36',
                                    '54PK': '54'
                                };

                                const bagDisplays = product.variants?.map(variant => {
                                    const sku = variant.sku || '';
                                    // Match new format: 11PK, 12PK, etc.
                                    const match = sku.toUpperCase().match(/(\d+PK)/);
                                    if (match) {
                                        const packSize = match[1];
                                        return SKU_BAG_MAPPING[packSize] || null;
                                    }
                                    return null;
                                }).filter(Boolean);

                                // Remove duplicates and return the display string
                                const uniqueBagDisplays = [...new Set(bagDisplays)];
                                let bagCountText = '';

                                if (uniqueBagDisplays.length === 1) {
                                    const singleDisplay = uniqueBagDisplays[0];
                                    // Return as-is if it's the special case, otherwise add "Bags"
                                    bagCountText = (singleDisplay === '12 Bags + 2 Free') ? singleDisplay : `${singleDisplay} Bags`;
                                } else if (uniqueBagDisplays.length > 1) {
                                    // Check if any item is a special case
                                    const specialCases = uniqueBagDisplays.filter(item => item === '12 Bags + 2 Free');
                                    const regularCases = uniqueBagDisplays.filter(item => item !== '12 Bags + 2 Free');

                                    if (specialCases.length > 0) {
                                        // If we have special cases, handle them separately
                                        if (regularCases.length === 0) {
                                            // Only special cases
                                            bagCountText = specialCases.join(' or ');
                                        } else if (regularCases.length === 1) {
                                            bagCountText = `${regularCases[0]} Bags or ${specialCases.join(' or ')}`;
                                        } else {
                                            const lastRegular = regularCases.pop();
                                            bagCountText = `${regularCases.join(', ')}, ${lastRegular} Bags or ${specialCases.join(' or ')}`;
                                        }
                                    } else {
                                        // No special cases, just regular numbers - consolidate "Bags"
                                        const lastNumber = uniqueBagDisplays.pop();
                                        bagCountText = uniqueBagDisplays.length > 0
                                            ? `${uniqueBagDisplays.join(', ')} or ${lastNumber} Bags`
                                            : `${lastNumber} Bags`;
                                    }
                                }

                                // Only render the element if there's actual content
                                return bagCountText ? p({ className: 'product-card__header-right' }, bagCountText) : null;
                            })(),
                        ),

                        // Product information
                        div({ className: 'product-card__info' },
                            // Vendor (if enabled)
                            showVendor && product.vendor &&
                            p({ className: 'product-card__vendor' }, product.vendor),

                            // Product title
                            h3({ className: 'product-card__title' }, product.title),

                            // Price information
                            div({ className: 'product-card__price' },
                                // Compare at price (strikethrough) - show if enabled and exists
                                (showComparePrice && product.compare_at_price && product.compare_at_price > product.price) &&
                                span({ className: 'price price--compare' }, formatPrice(product.compare_at_price)),
                                // Regular price
                                span({ className: 'price' }, formatPrice(product.price))
                            ),
                        ),

                        // Conditional buttons based on showQuickBuy setting
                        showQuickBuy ? (
                            // Quick Shop Mode: Show QUICK SHOP button + VIEW PRODUCT link
                            div({ className: 'product-card__bottom-wrapper' },
                                button({
                                    onClick: handleQuickShopClick,
                                    className: 'product-card__view-button',
                                    'aria-label': `Quick shop ${product.title}`
                                }, 'QUICK SHOP'),

                                a({
                                    href: (() => {
                                        const selectedVariant = product.variants?.[selectedVariantIndex];
                                        const baseUrl = `/products/${product.handle}`;
                                        return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                    })(),
                                    className: 'product-card__view-product-link product-card__view-product-link--front'
                                }, 'VIEW PRODUCT')
                            )
                        ) : (
                            // Simple Mode: Show SHOP NOW button only
                            div({ className: 'product-card__bottom-wrapper' },
                                a({
                                    href: (() => {
                                        const selectedVariant = product.variants?.[selectedVariantIndex];
                                        const baseUrl = `/products/${product.handle}`;
                                        return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                    })(),
                                    className: 'product-card__view-button'
                                }, 'SHOP NOW')
                            )
                        )
                    ),

                    // BACK SIDE (New flip content)
                    div({ className: 'product-card__back' },
                        // Header with close button
                        div({ className: 'product-card__back-header' },
                            h3({ className: 'product-card__back-title' },
                                (() => {
                                    // Show title with size information
                                    if (product.variantSize) {
                                        return `SUNFLOWER SEEDS | ${product.variantSize}`;
                                    }
                                    return 'SUNFLOWER SEEDS';
                                })()
                            ),
                            button({
                                className: 'product-card__close-btn',
                                onClick: handleCloseFlip,
                                'aria-label': 'Close details'
                            },
                                jsx('svg', {
                                    width: '12',
                                    height: '12',
                                    viewBox: '0 0 12 12',
                                    fill: 'none',
                                    xmlns: 'http://www.w3.org/2000/svg'
                                },
                                    jsx('path', {
                                        d: 'M1 1L11 11M11 1L1 11',
                                        stroke: 'currentColor',
                                        strokeWidth: '2',
                                        strokeLinecap: 'round'
                                    })
                                )
                            )
                        ),

                        // Product details
                        div({ className: 'product-card__back-content' },
                            // Product title (the actual product name)
                            h2({ className: 'product-card__back-product-title' }, product.title),

                            // Bag sizes row (selectable)
                            (() => {
                                // SKU bag count mapping for new format: 11PK, 12PK, 36PK, 54PK
                                const SKU_BAG_MAPPING = {
                                    '6PK': '6',
                                    '11PK': '11',
                                    '12PK': '12',
                                    '14PK': '12 Bags + 2 Free',
                                    '36PK': '36',
                                    '54PK': 'Bucket Of 54 Bags'
                                };

                                if (!product.variants || product.variants.length === 0) return null;

                                // Create array of variants with their bag display info
                                const variantBagInfo = product.variants.map((variant, index) => {
                                    const sku = variant.sku || '';
                                    // Match new format: 11PK, 12PK, etc.
                                    const match = sku.toUpperCase().match(/(\d+PK)/);
                                    if (match) {
                                        const packSize = match[1];
                                        const bagDisplay = SKU_BAG_MAPPING[packSize];
                                        if (bagDisplay) {
                                            const displayText = (bagDisplay === '12 Bags + 2 Free' || bagDisplay === 'Bucket Of 54 Bags')
                                                ? bagDisplay
                                                : `${bagDisplay} Bags`;
                                            return {
                                                index,
                                                variant,
                                                displayText,
                                                bagDisplay,
                                                packSize
                                            };
                                        }
                                    }
                                    return null;
                                }).filter(Boolean);

                                // Remove duplicates by display text but keep first occurrence
                                const uniqueVariantBagInfo = [];
                                const seenDisplayTexts = new Set();

                                variantBagInfo.forEach(info => {
                                    if (!seenDisplayTexts.has(info.displayText)) {
                                        seenDisplayTexts.add(info.displayText);
                                        uniqueVariantBagInfo.push(info);
                                    }
                                });

                                // Only render if there are bag sizes to show
                                if (uniqueVariantBagInfo.length === 0) return null;

                                return div({ className: 'product-card__back-bag-sizes' },
                                    uniqueVariantBagInfo.map((info, displayIndex) => {
                                        const isSelected = selectedVariantIndex === info.index;

                                        // Calculate price per ounce for this variant
                                        const variantPrice = info.variant.price || 0;

                                        // Extract bag count from pack size (e.g., "12PK" -> 12)
                                        const packSizeMatch = info.packSize.match(/(\d+)PK/);
                                        let totalOunces = 0;

                                        if (packSizeMatch) {
                                            const bagCount = parseInt(packSizeMatch[1]);

                                            // Determine ounces per bag based on SKU size indicator
                                            const sku = info.variant.sku.toUpperCase();
                                            let ouncesPerBag = 1.5; // Default to 1.5 oz

                                            if (sku.includes('4OZ')) {
                                                ouncesPerBag = 4;
                                            } else if (sku.includes('1.5OZ') || sku.includes('1OZ')) {
                                                ouncesPerBag = 1.5;
                                            }
                                            // For variety packs, keep default 1.5 oz

                                            totalOunces = bagCount * ouncesPerBag;
                                        }

                                        // Calculate price per ounce (price is in cents, so divide by 100 first)
                                        const pricePerOz = totalOunces > 0 ?
                                            formatPrice(Math.round((variantPrice / totalOunces))) :
                                            formatPrice(0);

                                        return button({
                                            key: info.displayText,
                                            className: `product-card__back-bag-size ${isSelected ? 'product-card__back-bag-size--selected' : ''}`,
                                            onClick: () => setSelectedVariantIndex(info.index),
                                            'aria-pressed': isSelected,
                                            'aria-label': `Select ${info.displayText} variant`
                                        },
                                            div({ className: 'product-card__back-bag-size-content' },
                                                div({ className: 'product-card__back-bag-size-text' }, info.displayText),
                                                div({ className: 'product-card__back-bag-size-price' }, pricePerOz + ' / OZ')
                                            )
                                        );
                                    })
                                );
                            })(),

                            // ONE-TIME / SUBSCRIBE toggle
                            // div({ className: 'product-card__back-purchase-options' },
                            //     div({ className: 'product-card__back-toggle-container' },
                            //         button({
                            //             className: 'product-card__back-toggle-option product-card__back-toggle-option--active'
                            //         }, 'ONE-TIME'),
                            //         button({
                            //             className: 'product-card__back-toggle-option'
                            //         }, 'SUBSCRIBE')
                            //     )
                            // ),

                            // Feature list with X marks
                            div({ className: 'product-card__back-features' },
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'FREE SHIPPING')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, '15% OFF EVERY ORDER')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'PAUSE, EDIT, OR CANCEL ANYTIME')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'VIP DISCOUNTS AND PERKS')
                                )
                            ),
                        ),

                        // Large price display
                        div({ className: 'product-card__back-large-price' },
                            (() => {
                                const selectedVariant = product.variants?.[selectedVariantIndex] || product.variants?.[0];
                                const variantPrice = selectedVariant?.price || product.price;
                                return formatPrice(variantPrice);
                            })()
                        ),

                        // Action buttons
                        div({ className: 'product-card__back-actions' },
                            button({
                                className: 'product-card__add-to-cart-btn',
                                onClick: handleQuickAdd,
                                disabled: isAddingToCart,
                                'aria-busy': isAddingToCart
                            }, isAddingToCart ? 'ADDING...' : 'ADD TO CART'),

                            a({
                                href: (() => {
                                    const selectedVariant = product.variants?.[selectedVariantIndex];
                                    const baseUrl = `/products/${product.handle}`;
                                    return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                })(),
                                className: 'product-card__view-product-link'
                            }, 'VIEW PRODUCT')
                        )
                    )
                )
            );
        };

        /**
         * Promotional Block Component
         * Renders promotional images or videos within the product grid
         */
        const PromotionalBlock = ({ block }) => {
            const overlayStyle = {
                backgroundColor: block.overlayColor,
                opacity: block.overlayOpacity / 100
            };

            const contentStyle = {
                color: block.textColor
            };

            const renderContent = () => {
                if (block.type === 'promotion_video') {
                    // Handle video content
                    if (block.video) {
                        // Shopify hosted video
                        return jsx('video', {
                            autoPlay: true,
                            muted: true,
                            loop: true,
                            playsInline: true,
                            className: 'promotional-block__video'
                        }, jsx('source', { src: block.video, type: 'video/mp4' }));
                    } else if (block.externalVideoUrl) {
                        // External video (YouTube/Vimeo iframe)
                        const videoId = block.externalVideoUrl.includes('youtube')
                            ? block.externalVideoUrl.split('v=')[1]?.split('&')[0]
                            : block.externalVideoUrl.split('/').pop();

                        const embedUrl = block.externalVideoUrl.includes('youtube')
                            ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`
                            : `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`;

                        return jsx('iframe', {
                            src: embedUrl,
                            className: 'promotional-block__iframe',
                            frameBorder: '0',
                            allow: 'autoplay; fullscreen; picture-in-picture',
                            allowFullScreen: true
                        });
                    }
                } else {
                    // Handle image content
                    const imageSrc = window.innerWidth <= 700 && block.mobileImage
                        ? block.mobileImage
                        : block.image;

                    if (imageSrc) {
                        return img({
                            src: imageSrc,
                            alt: block.title || 'Promotional content',
                            className: 'promotional-block__image'
                        });
                    }
                }
                return null;
            };

            const blockContent = div({ className: 'promotional-block__content-wrapper' },
                block.overlayColor && div({
                    className: 'promotional-block__overlay',
                    style: overlayStyle
                }),
                div({
                    className: `promotional-block__text-content ${block.textPosition}`,
                    style: contentStyle
                },
                    block.title && jsx(block.headingTag, {
                        className: 'promotional-block__title'
                    }, block.title),
                    block.content && div({
                        className: 'promotional-block__content',
                        dangerouslySetInnerHTML: { __html: block.content }
                    })
                    // Removed separate link button - entire block will be clickable
                )
            );

            // If link URL is provided, make entire block clickable
            const blockInner = div({ className: 'promotional-block__inner' },
                renderContent(),
                blockContent
            );

            const blockElement = block.linkUrl
                ? a({
                    href: block.linkUrl,
                    className: `promotional-block promotional-block--${block.type} promotional-block--span-${block.columnSpan} promotional-block--linked`,
                    id: `block-${block.id}`,
                    'aria-label': block.title || 'Promotional content'
                }, blockInner)
                : div({
                    className: `promotional-block promotional-block--${block.type} promotional-block--span-${block.columnSpan}`,
                    id: `block-${block.id}`
                }, blockInner);

            return blockElement;
        };

        /**
         * Sort Dropdown Component
         * Handles product sorting options
         */
        const SortDropdown = ({ sortBy, setSortBy, className = '' }) => {
            const sortOptions = [
                { value: 'created-descending', label: 'Date, new to old' },
                { value: 'created-ascending', label: 'Date, old to new' },
                { value: 'price-low-to-high', label: 'Price, low to high' },
                { value: 'price-high-to-low', label: 'Price, high to low' },
                { value: 'title-ascending', label: 'Alphabetically, A-Z' },
                { value: 'title-descending', label: 'Alphabetically, Z-A' }
            ];

            return select({
                value: sortBy,
                onChange: (e) => setSortBy(e.target.value),
                className: `sort-dropdown ${className}`.trim(),
                'aria-label': 'Sort products'
            },
                sortOptions.map(optionData =>
                    option({ key: optionData.value, value: optionData.value }, optionData.label)
                )
            );
        };

        /**
         * Filter Select Component
         * Reusable select component for filters
         */
        const FilterSelect = ({ value, onChange, options, placeholder, ariaLabel }) => {
            return select({
                value: value,
                onChange: (e) => onChange(e.target.value),
                className: 'filter-select',
                'aria-label': ariaLabel
            },
                option({ value: '' }, placeholder),
                options.map(optionValue =>
                    option({ key: optionValue, value: optionValue }, optionValue)
                )
            );
        };

        /**
         * Filter Bubble Component
         * Creates checkbox-style filter bubbles for multi-select
         */
        const FilterBubble = ({ label, selectedValues, options, onChange }) => {
            const handleToggle = (value) => {
                const newValues = selectedValues.includes(value)
                    ? selectedValues.filter(v => v !== value)
                    : [...selectedValues, value];
                onChange(newValues);
            };

            return div({ className: 'filter-bubble-group' },
                // Only show label if it's provided and not empty
                label && span({ className: 'filter-bubble-group__label' }, label),
                div({ className: 'filter-bubble-options' },
                    options.map(optionData => {
                        const optionValue = typeof optionData === 'string' ? optionData : optionData.value;
                        const optionLabel = typeof optionData === 'string' ? optionData : optionData.label;
                        const isSelected = selectedValues.includes(optionValue);

                        return button({
                            key: optionValue,
                            className: `filter-bubble-option ${isSelected ? 'filter-bubble-option--selected' : ''}`,
                            onClick: () => handleToggle(optionValue),
                            'aria-pressed': isSelected,
                            'aria-label': `${isSelected ? 'Remove' : 'Add'} ${optionLabel} filter`
                        }, optionLabel);
                    })
                )
            );
        };

        /**
         * Product Controls Component
         * Contains filtering controls and page title (sorting hidden from UI)
         */
        const ProductControls = ({
            filterBy, setFilterBy,
            vendors, productTypes, availableSKUFilters = [],
            enableFiltering = true,
            disableVendorFilter = false,
            disableAvailabilityFilter = false,
            pageTitle = ''
        }) => {
            // Don't render anything if filtering is disabled and no page title
            if (!enableFiltering && !pageTitle) {
                return null;
            }

            // Force show filter section if we have any available filters, even if all specific filters are disabled
            const hasAnyFilters = availableSKUFilters.length > 0 || vendors.length > 0 || productTypes.length > 0;

            return div({ className: 'product-controls-section' },
                // Controls container
                div({ className: 'product-controls' },
                    // Left side - Page title only
                    div({ className: 'product-controls__left' },
                        pageTitle && h1({ className: 'page-title' }, pageTitle)
                    ),

                    // Right side - Filter bubbles
                    enableFiltering && div({ className: 'product-controls__filters' },
                        // "Shop by:" label and filters container - always show when filtering is enabled
                        div({ className: 'filter-section' },
                            span({ className: 'filter-section__label' }, 'Shop by:'),
                            div({ className: 'filter-bubbles-container' },
                                // SKU-based filter bubble (replaces tag-based filtering)
                                !disableAvailabilityFilter && availableSKUFilters.length > 0 && jsx(FilterBubble, {
                                    label: '', // Remove the individual filter label
                                    selectedValues: filterBy.skuFilters || [],
                                    onChange: (values) => setFilterBy(prev => ({ ...prev, skuFilters: values })),
                                    options: availableSKUFilters.map(filterLabel => ({
                                        value: filterLabel,
                                        label: filterLabel // Use the filter label as-is (e.g., "1.5 oz", "4 oz", "Variety Pack")
                                    }))
                                }),

                                // Vendor filter bubble (conditionally rendered)
                                !disableVendorFilter && vendors.length > 0 && jsx(FilterBubble, {
                                    label: '', // Remove the individual filter label
                                    selectedValues: filterBy.vendor,
                                    onChange: (values) => setFilterBy(prev => ({ ...prev, vendor: values })),
                                    options: vendors
                                }),

                                // Product type filter bubble
                                productTypes.length > 0 && jsx(FilterBubble, {
                                    label: '', // Remove the individual filter label
                                    selectedValues: filterBy.productType,
                                    onChange: (values) => setFilterBy(prev => ({ ...prev, productType: values })),
                                    options: productTypes
                                })
                            )
                        )
                    )
                )
            );
        };

        /**
         * Product Grid Component
         * Handles filtering and rendering of products and promotional blocks (sorting handled by parent)
         */
        const ProductGrid = ({ products, filterBy, showVendor, showQuickBuy = true, enableFiltering = true, promotionalBlocks = [], showComparePrice = true }) => {
            // Products are already sorted and filtered by parent component
            if (products.length === 0) {
                return div({ className: 'product-grid-empty' },
                    p(null, 'No products found matching your criteria.')
                );
            }

            // Create array of all items (products + promotional blocks) based on positions
            const createGridItems = () => {
                const items = [];
                let productIndex = 0;

                // Sort promotional blocks by position
                const sortedBlocks = [...promotionalBlocks].sort((a, b) => a.position - b.position);

                // Determine the maximum position we need to consider
                const maxPosition = Math.max(
                    products.length,
                    sortedBlocks.length > 0 ? Math.max(...sortedBlocks.map(b => b.position)) : 0
                );

                for (let position = 1; position <= maxPosition; position++) {
                    // Check if there's a promotional block at this position
                    const blockAtPosition = sortedBlocks.find(block => block.position === position);

                    if (blockAtPosition) {
                        // Add promotional block
                        items.push({
                            type: 'promotional',
                            key: `promo-${blockAtPosition.id}`,
                            block: blockAtPosition
                        });
                    } else if (productIndex < products.length) {
                        // Add product
                        items.push({
                            type: 'product',
                            key: `product-${products[productIndex].id}`,
                            product: products[productIndex]
                        });
                        productIndex++;
                    }
                }

                // Add any remaining products if we have more products than positions with promotional blocks
                while (productIndex < products.length) {
                    items.push({
                        type: 'product',
                        key: `product-${products[productIndex].id}`,
                        product: products[productIndex]
                    });
                    productIndex++;
                }

                return items;
            };

            const gridItems = createGridItems();

            return div({ className: 'product-list' },
                gridItems.map(item => {
                    if (item.type === 'promotional') {
                        return jsx(PromotionalBlock, {
                            key: item.key,
                            block: item.block
                        });
                    } else {
                        return jsx(ProductCard, {
                            key: item.key,
                            product: item.product,
                            showVendor,
                            showQuickBuy,
                            showComparePrice,
                            selectedSKUFilters: filterBy.skuFilters || []
                        });
                    }
                })
            );
        };

        /**
         * Pagination Component
         * Handles page navigation
         */
        const Pagination = ({ currentPage, totalPages, setCurrentPage, gridRef }) => {
            if (totalPages <= 1) return null;

            // Handle page change with scroll to grid
            const handlePageChange = (newPage) => {
                setCurrentPage(newPage);

                // Scroll after state update using setTimeout
                setTimeout(() => {
                    if (gridRef && gridRef.current) {
                        gridRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                }, 0);
            };

            const generatePageNumbers = () => {
                const pages = [];
                const maxVisible = 5;

                if (totalPages <= maxVisible) {
                    // Show all pages if total is small
                    for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                    }
                } else {
                    // Show smart pagination with ellipsis
                    pages.push(1);

                    if (currentPage > 3) {
                        pages.push('...');
                    }

                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);

                    for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                            pages.push(i);
                        }
                    }

                    if (currentPage < totalPages - 2) {
                        pages.push('...');
                    }

                    if (totalPages > 1) {
                        pages.push(totalPages);
                    }
                }

                return pages;
            };

            const pageNumbers = generatePageNumbers();

            return nav({ className: 'pagination', 'aria-label': 'Product pagination' },
                // Previous button
                button({
                    onClick: () => handlePageChange(Math.max(1, currentPage - 1)),
                    disabled: currentPage === 1,
                    className: 'pagination__prev',
                    'aria-label': 'Go to previous page'
                }, 'Previous'),

                // Page numbers
                pageNumbers.map((page, index) => {
                    if (page === '...') {
                        return span({
                            key: `ellipsis-${index}`,
                            className: 'pagination__ellipsis'
                        }, '...');
                    }

                    return button({
                        key: page,
                        onClick: () => handlePageChange(page),
                        className: `pagination__item ${page === currentPage ? 'pagination__item--current' : ''}`,
                        'aria-label': `Go to page ${page}`,
                        'aria-current': page === currentPage ? 'page' : undefined
                    }, page);
                }),

                // Next button
                button({
                    onClick: () => handlePageChange(Math.min(totalPages, currentPage + 1)),
                    disabled: currentPage === totalPages,
                    className: 'pagination__next',
                    'aria-label': 'Go to next page'
                }, 'Next')
            );
        };

        /**
         * Main React Product Widget Component
         * Orchestrates all components and manages state
         */
        const ReactProductWidget = ({ config }) => {
            // State management
            const [filterBy, setFilterBy] = useState({
                skuFilters: [], // Changed from tags to skuFilters
                vendor: [],
                productType: []
            });
            const [currentPage, setCurrentPage] = useState(1);

            // Create ref for product grid to enable scroll to grid on pagination
            const gridRef = React.useRef(null);

            // Configuration - get default sort from config
            const productsPerPage = config.productsPerPage || 24;
            const disablePagination = config.disablePagination || false; // Get pagination setting from config
            const originalProducts = config.initialProducts || [];
            const showAllProducts = config.showAllProducts !== false; // Default to true if not specified
            const products = expandProductsByVariant(originalProducts, showAllProducts); // Expand products by variants
            const promotionalBlocks = config.promotionalBlocks || []; // Get promotional blocks from config
            const showVendor = config.cardSettings?.showVendor || false;
            const showQuickBuy = config.cardSettings?.showQuickBuy !== false; // Default to true
            const showComparePrice = config.cardSettings?.showComparePrice !== false; // Default to true
            const enableFiltering = config.enableFiltering !== false; // Default to true

            // Debug logging
            console.log('Collection Widget Config:', {
                showQuickBuy: config.cardSettings?.showQuickBuy,
                showComparePrice: config.cardSettings?.showComparePrice
            });
            const disableVendorFilter = config.disableVendorFilter || false; // Get from theme editor
            const disableAvailabilityFilter = config.disableAvailabilityFilter || false; // Get from theme editor
            const availableSKUFilters = getAvailableSKUFilters(originalProducts); // Get available SKU filters from original products
            const pageTitle = config.pageTitle || '';
            const sortBy = config.defaultSort || 'created-descending'; // Get from theme editor

            // Reset to first page when filters change (only if pagination is enabled)
            useEffect(() => {
                if (!disablePagination) {
                    setCurrentPage(1);
                }
            }, [filterBy, disablePagination]);

            // Get filtered and sorted products for pagination calculation
            const processedProducts = useMemo(() => {
                let filtered = [...products];

                // Apply filters (only if filtering is enabled)
                if (enableFiltering) {
                    // SKU-based filtering (works with variant-specific products)
                    if (filterBy.skuFilters && filterBy.skuFilters.length > 0) {
                        filtered = filtered.filter(p => {
                            // If this is a variant-specific product, check if its size matches the filter
                            if (p.variantSize) {
                                return filterBy.skuFilters.includes(p.variantSize);
                            }
                            // Otherwise, use the original matching logic
                            return productMatchesSKUFilter(p, filterBy.skuFilters);
                        });
                    }

                    if (!disableVendorFilter && filterBy.vendor.length > 0) {
                        filtered = filtered.filter(p => filterBy.vendor.includes(p.vendor));
                    }

                    if (filterBy.productType.length > 0) {
                        filtered = filtered.filter(p => filterBy.productType.includes(p.product_type));
                    }
                }

                // Apply sorting based on configuration
                const sortProducts = (products) => {
                    const sorted = [...products];

                    // Primary sort based on configuration
                    switch (sortBy) {
                        case 'price-low-to-high':
                            sorted.sort((a, b) => a.price - b.price);
                            break;
                        case 'price-high-to-low':
                            sorted.sort((a, b) => b.price - a.price);
                            break;
                        case 'title-ascending':
                            sorted.sort((a, b) => a.title.localeCompare(b.title));
                            break;
                        case 'title-descending':
                            sorted.sort((a, b) => b.title.localeCompare(a.title));
                            break;
                        case 'created-ascending':
                            sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                            break;
                        case 'created-descending':
                        default:
                            sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                            break;
                    }

                    // Secondary sort by variant size to group sizes together
                    const sizeOrder = ['1.5 oz', '4 oz', 'Variety Pack'];
                    return sorted.sort((a, b) => {
                        // If both have variant sizes, sort by size order
                        if (a.variantSize && b.variantSize) {
                            const aIndex = sizeOrder.indexOf(a.variantSize);
                            const bIndex = sizeOrder.indexOf(b.variantSize);

                            // If both sizes are in our order list, sort by that order
                            if (aIndex !== -1 && bIndex !== -1) {
                                return aIndex - bIndex;
                            }
                            // If only one is in the list, prioritize it
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            // If neither is in the list, alphabetical
                            return a.variantSize.localeCompare(b.variantSize);
                        }

                        // If only one has variant size, no change in order (keep primary sort)
                        return 0;
                    });
                };

                return sortProducts(filtered);
            }, [products, filterBy, sortBy, enableFiltering, disableVendorFilter, disableAvailabilityFilter]);

            // Paginated products - show all products if pagination is disabled
            const paginatedProducts = useMemo(() => {
                if (disablePagination) {
                    // Show all products when pagination is disabled
                    return processedProducts;
                }

                const startIndex = (currentPage - 1) * productsPerPage;
                const endIndex = startIndex + productsPerPage;
                return processedProducts.slice(startIndex, endIndex);
            }, [processedProducts, currentPage, productsPerPage, disablePagination]);

            const totalPages = disablePagination ? 1 : Math.ceil(processedProducts.length / productsPerPage);

            // Get unique values for filters (use original products to avoid duplicates)
            const vendors = useMemo(() => getUniqueValues(originalProducts, 'vendor'), [originalProducts]);
            const productTypes = useMemo(() => getUniqueValues(originalProducts, 'product_type'), [originalProducts]);

            return div({ className: `react-product-widget ${disablePagination ? 'react-product-widget--no-pagination' : ''} ${!showQuickBuy ? 'react-product-widget--no-quick-buy' : ''}` },
                // Controls
                jsx(ProductControls, {
                    filterBy,
                    setFilterBy,
                    vendors,
                    productTypes,
                    availableSKUFilters,
                    enableFiltering,
                    disableVendorFilter,
                    disableAvailabilityFilter,
                    pageTitle
                }),

                // Scroll anchor for pagination
                div({ ref: gridRef, style: { position: 'relative', top: '-20px' } }),

                // Product Grid
                jsx(ProductGrid, {
                    products: paginatedProducts,
                    filterBy,
                    showVendor,
                    showQuickBuy,
                    enableFiltering,
                    promotionalBlocks,
                    showComparePrice
                }),

                // Pagination - only render if pagination is enabled
                !disablePagination && jsx(Pagination, {
                    currentPage,
                    totalPages,
                    setCurrentPage,
                    gridRef
                })
            );
        };

        // =============================================================================
        // INITIALIZATION
        // =============================================================================

        /**
         * Initialize the React Product Widget
         */
        function initializeReactProductWidget() {
            const configScript = document.querySelector('script[data-react-product-config]');
            if (!configScript) {
                console.error('React product config not found');
                return;
            }

            try {
                const config = JSON.parse(configScript.textContent);
                const container = document.getElementById(config.containerId);

                if (!container) {
                    console.error('React product container not found:', config.containerId);
                    return;
                }

                // Create and render the widget
                const root = ReactDOM.createRoot(container);
                root.render(jsx(ReactProductWidget, { config }));

            } catch (error) {
                console.error('Failed to initialize React product widget:', error);
            }
        }

        // Initialize when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initializeReactProductWidget);
        } else {
            initializeReactProductWidget();
        }

        // Export for potential external use
        window.ReactCollectionWidget = {
            init: initializeReactProductWidget,
            components: {
                ReactProductWidget,
                ProductCard,
                ProductGrid,
                Pagination,
                ProductControls,
                FilterBubble,
                PromotionalBlock
            }
        };
    }    // Wait for React to load (it's loaded globally in theme.liquid)
    // Widgets always run with defer, so React will be available
    if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined') {
        initWidget();
    } else {
        console.error('Main Collection Widget: React not available. Ensure React is loaded in theme.liquid');
    }

})();
